import { useEffect, useRef, useState, type FormEvent } from "react";
import { getDealerPermissions, normalizeDealerPermissions, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, type DealerPermission } from "@longjie/shared";
import { ApiError, request } from "./api";
import { Button, Field, LoadError, Modal } from "./components";
import { DealerPermissionPicker } from "./AccountPermissions";
import { PermissionPresets } from "./PermissionPresets";
import type { Address, Customer } from "./types";

type Snapshot = { customer: Customer; addresses: Address[] };
type Profile = Pick<Customer, "code" | "name" | "contactName" | "phone" | "active" | "username">;
type AddressDraft = { key: string; id?: string; contactName: string; phone: string; detail: string; isDefault: boolean; active: boolean };
type AddressPatch = Partial<Omit<AddressDraft, "key">>;
type Patch = Partial<Profile> & { newPassword?: string; dealerPermissions?: DealerPermission[]; addresses?: AddressPatch[] };

function profileOf(customer: Customer): Profile {
  const { code, name, contactName, phone, active, username } = customer;
  return { code, name, contactName, phone, active, username };
}

function fullDetail(address: Address) {
  return `${address.province}${address.city}${address.district}${address.detail}`.trim();
}

function draftOf(address: Address): AddressDraft {
  return { key: address.id, id: address.id, contactName: address.contactName, phone: address.phone, detail: fullDetail(address), isDefault: address.isDefault, active: address.active ?? true };
}

export function CustomerEditorModal({ customer, canManageCredentials, onClose, onSaved }: {
  customer: Customer;
  canManageCredentials: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [profile, setProfile] = useState(() => profileOf(customer));
  const [addresses, setAddresses] = useState<AddressDraft[]>([]);
  const [permissions, setPermissions] = useState<DealerPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [viewedPassword, setViewedPassword] = useState<string | null>(null);
  const [viewMessage, setViewMessage] = useState("");
  const [viewError, setViewError] = useState("");
  const [viewLoading, setViewLoading] = useState(false);
  const viewController = useRef<AbortController | null>(null);
  const path = `/api/v1/admin/customers/${customer.id}/edit`;
  const ready = !loading && !loadError && snapshot?.customer.id === customer.id;

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;
    void request<Snapshot>(path, { signal: controller.signal, cache: "no-store" }).then((result) => {
      if (!mounted) return;
      if (!result.customer || result.customer.id !== customer.id || !Array.isArray(result.addresses)) throw new Error("客户资料或地址数据不完整");
      setSnapshot(result);
      setProfile(profileOf(result.customer));
      setAddresses(result.addresses.map(draftOf));
      setPermissions(getDealerPermissions(result.customer));
      setLoadError("");
    }).catch((caught: unknown) => {
      if (mounted) setLoadError(caught instanceof ApiError ? caught.message : "客户资料和收货地址加载失败，请重试");
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; controller.abort(); viewController.current?.abort(); };
  }, [path, customer.id, attempt]);

  function retry() {
    setLoading(true);
    setLoadError("");
    setSnapshot(null);
    setAttempt((value) => value + 1);
  }

  function close() { if (!savingRef.current) onClose(); }

  async function viewPassword() {
    if (!canManageCredentials || viewLoading || savingRef.current) return;
    const controller = new AbortController();
    viewController.current = controller;
    setViewedPassword(null);
    setViewError("");
    setViewMessage("");
    setViewLoading(true);
    try {
      const result = await request<{ available: boolean; password: string | null }>(`/api/v1/admin/customers/${customer.id}/password`, { cache: "no-store", signal: controller.signal });
      if (controller.signal.aborted) return;
      if (result.available && result.password !== null) setViewedPassword(result.password);
      else setViewMessage("这个账号还没有可查看的密码记录。以后修改或重新设置密码后，后台才可查看。");
    } catch (caught) {
      if (!controller.signal.aborted) setViewError(caught instanceof ApiError ? caught.message : "当前密码暂时无法查看");
    } finally { if (!controller.signal.aborted) setViewLoading(false); }
  }

  function selectDefault(key: string) {
    setAddresses((current) => current.map((address) => ({ ...address, isDefault: address.key === key && address.active })));
  }

  function changeAddress(key: string, values: Partial<AddressDraft>) {
    setAddresses((current) => {
      const next = current.map((address) => address.key === key ? { ...address, ...values, ...(values.active === false ? { isDefault: false } : {}) } : address);
      if (values.active === false && current.some((address) => address.key === key && address.isDefault)) {
        const replacement = next.find((address) => address.active);
        if (replacement) replacement.isDefault = true;
      }
      return next;
    });
  }

  function addAddress() {
    setAddresses((current) => [...current, { key: crypto.randomUUID(), contactName: profile.contactName, phone: profile.phone, detail: "", active: true, isDefault: !current.some((address) => address.active && address.isDefault) }]);
  }

  function removeAddress(key: string) {
    setAddresses((current) => {
      const removed = current.find((address) => address.key === key);
      const next = current.filter((address) => address.key !== key || address.id);
      if (removed && !removed.id && removed.isDefault) {
        const replacement = next.find((address) => address.active);
        return next.map((address) => ({ ...address, isDefault: address.key === replacement?.key }));
      }
      return next;
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || !snapshot || savingRef.current) return;
    setError("");
    const patch: Patch = {};
    for (const key of ["code", "name", "contactName", "phone"] as const) {
      const value = profile[key].trim();
      if (!value) { setError("客户编码、客户名称、联系人和联系电话不能为空"); return; }
      if (value !== snapshot.customer[key].trim()) patch[key] = value;
    }
    if (!/^[A-Za-z0-9_-]+$/.test(profile.code.trim())) { setError("客户编码只能包含字母、数字、横线和下划线"); return; }
    if (profile.active !== snapshot.customer.active) patch.active = profile.active;
    if (canManageCredentials) {
      const username = profile.username.trim();
      if (!username || !/^[A-Za-z0-9_.-]+$/.test(username)) { setError("登录账号只能包含字母、数字、下划线、点或短横线"); return; }
      if (username !== snapshot.customer.username) patch.username = username;
      if (newPassword) {
        if (newPassword.length < PASSWORD_MIN_LENGTH || newPassword.length > PASSWORD_MAX_LENGTH) { setError(`新密码需为 ${PASSWORD_MIN_LENGTH} 至 ${PASSWORD_MAX_LENGTH} 位`); return; }
        if (newPassword.toLowerCase() !== confirmation.toLowerCase()) { setError("两次输入的新密码不一致"); return; }
        patch.newPassword = newPassword;
      }
      const normalized = normalizeDealerPermissions(permissions);
      if (JSON.stringify(normalized) !== JSON.stringify(getDealerPermissions(snapshot.customer))) patch.dealerPermissions = normalized;
    }
    const changes: AddressPatch[] = [];
    for (const address of addresses) {
      const contactName = address.contactName.trim();
      const phone = address.phone.trim();
      const detail = address.detail.trim();
      if (!address.id) {
        if (!contactName || !phone || !detail) { setError("收货地址的联系人、联系电话和详细地址不能为空"); return; }
        changes.push({ contactName, phone, detail, isDefault: address.isDefault, active: true });
        continue;
      }
      const original = snapshot.addresses.find((item) => item.id === address.id);
      if (!original) { setError("收货地址数据已变化，请重新打开编辑"); return; }
      const change: AddressPatch = { id: address.id };
      if (contactName !== original.contactName.trim()) change.contactName = contactName;
      if (phone !== original.phone.trim()) change.phone = phone;
      if (detail !== fullDetail(original)) change.detail = detail;
      if (address.active !== (original.active ?? true)) change.active = address.active;
      if (address.isDefault !== original.isDefault) change.isDefault = address.isDefault;
      if (change.contactName === "" || change.phone === "" || change.detail === "") { setError("收货地址的联系人、联系电话和详细地址不能为空"); return; }
      if (Object.keys(change).length > 1) changes.push(change);
    }
    if (changes.length) patch.addresses = changes;
    if (!Object.keys(patch).length) { setError("请先修改客户资料、账号信息或收货地址"); return; }
    savingRef.current = true;
    setSaving(true);
    viewController.current?.abort();
    setViewLoading(false);
    try {
      await request(path, { method: "PATCH", body: JSON.stringify(patch) });
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "客户保存失败，请重试");
    } finally { savingRef.current = false; setSaving(false); }
  }

  return <Modal title="编辑客户" description={`${customer.name}：一起修改客户资料、登录信息和收货地址，保存后统一生效。`} onClose={close} footer={<>
    <Button variant="secondary" onClick={close} disabled={saving}>取消</Button>
    <Button form="customer-editor-form" type="submit" loading={saving} disabled={!ready}>保存修改</Button>
  </>}>
    {loading && <p role="status">正在加载客户资料和收货地址…</p>}
    {loadError && <LoadError title="客户资料加载失败" description={loadError} onRetry={retry} />}
    {ready && <form id="customer-editor-form" className="customer-editor form-grid" onSubmit={submit}>
      {error && <div className="inline-error form-grid__full" role="alert">{error}</div>}
      <section className="customer-editor__section form-grid__full" aria-label="客户资料">
        <div className="customer-editor__section-heading"><h3>客户资料</h3></div>
        <div className="form-grid">
          <Field label="客户编码"><input value={profile.code} onChange={(event) => setProfile({ ...profile, code: event.target.value })} maxLength={50} required disabled={saving} /></Field>
          <Field label="客户名称"><input value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} maxLength={160} required disabled={saving} /></Field>
          <Field label="联系人"><input value={profile.contactName} onChange={(event) => setProfile({ ...profile, contactName: event.target.value })} maxLength={80} required disabled={saving} /></Field>
          <Field label="联系电话"><input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} maxLength={30} required disabled={saving} /></Field>
          <label className="check-field form-grid__full"><input type="checkbox" checked={profile.active} onChange={(event) => setProfile({ ...profile, active: event.target.checked })} disabled={saving} />启用客户</label>
        </div>
      </section>
      {canManageCredentials && <section className="customer-editor__section form-grid__full" aria-label="登录账号和密码">
        <div className="customer-editor__section-heading"><h3>登录账号和密码</h3></div>
        <p className="field__hint">可查看已保存的当前密码，或直接设置新密码。修改后请使用最新登录信息。</p>
        <div className="form-grid">
          <Field label="登录账号"><input value={profile.username} onChange={(event) => setProfile({ ...profile, username: event.target.value })} autoComplete="off" maxLength={80} required disabled={saving} /></Field>
          <div className="form-grid__full">
            <Button type="button" variant="secondary" onClick={() => void viewPassword()} loading={viewLoading} disabled={saving}>查看当前密码</Button>
            {viewedPassword !== null && <Field label="当前密码"><input type="text" value={viewedPassword} readOnly autoComplete="off" /></Field>}
            {viewMessage && <p className="field__hint" role="status">{viewMessage}</p>}
            {viewError && <div className="inline-error" role="alert">{viewError}</div>}
          </div>
          <div className="form-grid__full">
            <Field label="新密码" hint={`选填；留空则保留当前密码。至少 ${PASSWORD_MIN_LENGTH} 位，可自由使用字母、数字和符号，不区分字母大小写，数字和符号原样匹配。`}><input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} autoComplete="new-password" disabled={saving} /></Field>
            <label className="check-field"><input type="checkbox" aria-label="显示新密码" checked={showNewPassword} onChange={(event) => setShowNewPassword(event.target.checked)} disabled={saving} />显示新密码</label>
          </div>
          {newPassword && <Field label="确认新密码"><input type={showNewPassword ? "text" : "password"} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} autoComplete="new-password" required disabled={saving} /></Field>}
        </div>
      </section>}
      <section className="customer-editor__section form-grid__full" aria-label="收货地址">
        <div className="customer-editor__section-heading"><h3>收货地址</h3><Button type="button" variant="secondary" onClick={addAddress} disabled={saving}>新增地址</Button></div>
        {!addresses.length && <p className="field__hint">还没有收货地址，可在这里新增。</p>}
        <div className="customer-editor__addresses">
          {addresses.map((address, index) => <fieldset key={address.key} className="customer-editor__address" disabled={saving}>
            <legend>地址 {index + 1}{!address.id ? "（新增）" : !address.active ? "（已停用）" : ""}</legend>
            <div className="form-grid">
              <Field label="联系人"><input value={address.contactName} onChange={(event) => changeAddress(address.key, { contactName: event.target.value })} maxLength={80} required={!address.id || address.contactName.trim() !== snapshot?.addresses.find((item) => item.id === address.id)?.contactName.trim()} /></Field>
              <Field label="联系电话"><input value={address.phone} onChange={(event) => changeAddress(address.key, { phone: event.target.value })} maxLength={30} required={!address.id || address.phone.trim() !== snapshot?.addresses.find((item) => item.id === address.id)?.phone.trim()} /></Field>
              <div className="form-grid__full"><Field label="详细地址"><textarea value={address.detail} onChange={(event) => changeAddress(address.key, { detail: event.target.value })} maxLength={300} required={!address.id || address.detail.trim() !== fullDetail(snapshot!.addresses.find((item) => item.id === address.id)!)} /></Field></div>
              <div className="customer-editor__address-actions form-grid__full">
                <label className="check-field"><input type="radio" name="customer-editor-default-address" checked={address.isDefault} disabled={!address.active} onChange={() => selectDefault(address.key)} />设为默认收货地址</label>
                {address.id ? <label className="check-field"><input type="checkbox" checked={address.active} onChange={(event) => changeAddress(address.key, { active: event.target.checked })} />启用此地址</label> : <Button type="button" variant="ghost" onClick={() => removeAddress(address.key)}>移除新增地址</Button>}
              </div>
            </div>
          </fieldset>)}
        </div>
      </section>
      {canManageCredentials && <section className="customer-editor__section form-grid__full" aria-label="客户权限">
        <div className="customer-editor__section-heading"><h3>客户权限</h3></div>
        <PermissionPresets accountType="DEALER" value={permissions} onApply={setPermissions} disabled={saving} />
        <DealerPermissionPicker value={permissions} onChange={setPermissions} disabled={saving} />
      </section>}
    </form>}
  </Modal>;
}
